<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { Resolved } from '@/types'
import { resolvePath, normalizePath, getService, PATHS } from '@/content'
import { useBlogStore, fetchBlogPage } from '@/lib/blog'

import HomePage from '@/pages/HomePage.vue'
import ServicePage from '@/pages/ServicePage.vue'
import ZonePage from '@/pages/ZonePage.vue'
import ArticlePage from '@/pages/ArticlePage.vue'
import BlogList from '@/pages/BlogList.vue'
import CategoryPage from '@/pages/CategoryPage.vue'
import ContactPage from '@/pages/ContactPage.vue'
import QuiSommesNous from '@/pages/QuiSommesNous.vue'
import MentionsLegales from '@/pages/MentionsLegales.vue'
import MerciPage from '@/pages/MerciPage.vue'
import NotFound from '@/pages/NotFound.vue'

const route = useRoute()
const blog = useBlogStore()
const pending = ref(false)

// Le blog est servi en direct : ses donnees arrivent dans le store (rendu serveur live,
// ou requete /__live/page lors d'une navigation interne). Le reste du site est statique.
const resolved = computed<Resolved>(() => {
  const path = normalizePath(route.path)
  const p = blog.payload
  if (p && p.path === path) {
    return {
      kind: p.kind,
      article: p.article,
      category: p.category,
      service: p.kind === 'blog' ? getService(PATHS.blog) : undefined,
    }
  }
  return resolvePath(path)
})

// Chemin pas encore connu cote navigateur (liste du blog, article, categorie, ou 404) :
// on demande au serveur live. Jamais au rendu serveur, ou les donnees sont deja la.
function needsLive(path: string): boolean {
  if (blog.payload?.path === path || blog.missPath === path) return false
  const kind = resolvePath(path).kind
  return kind === 'blog' || kind === 'not-found'
}

let seq = 0
watch(
  () => normalizePath(route.path),
  async (path) => {
    if (import.meta.env.SSR || !needsLive(path)) return
    const mine = ++seq
    pending.value = true
    try {
      const payload = await fetchBlogPage(path)
      if (mine !== seq) return
      if (payload) blog.payload = payload
      else blog.missPath = path
    } catch {
      if (mine === seq) blog.missPath = path
    } finally {
      if (mine === seq) pending.value = false
    }
  },
  { immediate: true },
)

const componentFor = computed(() => {
  switch (resolved.value.kind) {
    case 'home':
      return HomePage
    case 'service':
      // Les pages d'interventions locales (section 'zone') ont leur propre gabarit.
      return resolved.value.service?.section === 'zone' ? ZonePage : ServicePage
    case 'article':
      return ArticlePage
    case 'blog':
      return BlogList
    case 'category':
      return CategoryPage
    case 'contact':
      return ContactPage
    case 'qui-sommes-nous':
      return QuiSommesNous
    case 'mentions':
      return MentionsLegales
    case 'merci':
      return MerciPage
    default:
      return NotFound
  }
})
</script>

<template>
  <div v-if="pending" class="live-pending container" aria-busy="true">
    <p>Chargement de la page...</p>
  </div>
  <component v-else :is="componentFor" :key="route.path" :resolved="resolved" />
</template>

<style scoped>
.live-pending {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
}
</style>
